/**
 * Magnifies product gallery images around the pointer.
 *
 * A plain `scale()` magnifies the centre, so whatever someone is looking at slides away
 * from the cursor. Anchoring `transform-origin` to the pointer keeps the point under the
 * cursor still while everything expands around it — the behaviour people expect from a
 * product zoom.
 *
 * Attaches to the existing gallery markup rather than requiring a wrapper element, so no
 * upstream Horizon snippet has to be edited. Skipped on touch and for reduced motion.
 */

const SELECTOR = '.product-information__media .product-media-container';
const ZOOM = 2;

function anchor(frame, image, event) {
  const bounds = frame.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return;

  const x = ((event.clientX - bounds.left) / bounds.width) * 100;
  const y = ((event.clientY - bounds.top) / bounds.height) * 100;

  image.style.setProperty('--hover-zoom-x', `${Math.min(100, Math.max(0, x))}%`);
  image.style.setProperty('--hover-zoom-y', `${Math.min(100, Math.max(0, y))}%`);
}

function bind(frame) {
  if (frame.dataset.hoverZoomBound) return;
  frame.dataset.hoverZoomBound = 'true';

  // Looked up per interaction: the carousel swaps slides, so the image is not stable.
  const currentImage = () => frame.querySelector('img');

  frame.addEventListener('pointerenter', (event) => {
    const image = currentImage();
    if (!image) return;
    anchor(frame, image, event);
    image.style.setProperty('--hover-zoom-scale', String(ZOOM));
  });

  frame.addEventListener('pointermove', (event) => {
    const image = currentImage();
    if (image) anchor(frame, image, event);
  });

  frame.addEventListener('pointerleave', () => {
    const image = currentImage();
    if (image) image.style.setProperty('--hover-zoom-scale', '1');
  });
}

function init() {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll(SELECTOR).forEach(bind);

  // Sections re-render in the theme editor and when variants change, so pick up new frames.
  new MutationObserver(() => document.querySelectorAll(SELECTOR).forEach(bind))
    .observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
